/* eslint-disable jsx-a11y/no-noninteractive-element-interactions -- TODO: fix this error */
/* eslint-disable jsx-a11y/click-events-have-key-events -- TODO: fix this error */
import React, { useMemo, useState } from 'react';
import { AdminClientList } from 'shared_components/generated/admin';
import { useClientId } from 'shared_components/context/client';
import SquarePlaceholder100 from 'shared_components/public/assets/SquarePlaceholder100.svg';
import clsx from 'clsx';
import { useLocalStorageState } from 'shared_components/hooks/useLocalStorageState';

type SortOrder = 'asc' | 'desc';

const DEFAULT_SORT_ORDER: SortOrder = 'asc';

type ClientStatus = 'all' | 'client' | 'potential' | 'non-client';

const DEFAULT_CLIENT_STATUS: ClientStatus = 'all';

const getStatusColor = (artist: AdminClientList) => {
  if (
    artist.completedOnboardingSteps?.some(
      (step) => step.internalIdentifier === '3'
    )
  ) {
    return 'tw-border-westburyGreen tw-shadow-[0_0_12px_-2px_rgba(34,197,94,0.6)]';
  } else if (artist.nonClient) {
    return 'tw-border-westburyRed tw-shadow-[0_0_12px_-2px_rgba(239,68,68,0.6)]';
  } else if (
    !artist.completedOnboardingSteps?.some(
      (step) => step.internalIdentifier === '3'
    )
  ) {
    return 'tw-border-surfaceLime tw-shadow-[0_0_12px_-2px_rgba(234,179,8,0.6)]';
  }
};

// ArtistList component is responsible for displaying a list of artists with a search functionality.
// This component is reused in the SocietiesArtists component and the ArtistListBox component.
export const ArtistList = ({
  artists,
}: {
  readonly artists: AdminClientList[];
}) => {
  const [artistsSearch, setArtistsSearch] = useLocalStorageState<string>(
    'artist-list_search',
    '',
    { namespace: 'clients-list' }
  );
  const searchRegex = new RegExp(artistsSearch, 'i');
  const { clientId, setClientId } = useClientId();

  const [sortOrder, setSortOrder] = useLocalStorageState<SortOrder>(
    'artist-list_sort-order',
    DEFAULT_SORT_ORDER,
    { namespace: 'clients-list' }
  );
  const [selectedStatus, setSelectedStatus] =
    useLocalStorageState<ClientStatus>(
      'artist-list_selected-status',
      DEFAULT_CLIENT_STATUS,
      { namespace: 'clients-list' }
    );

  const filteredArtists = useMemo(() => {
    const result = artists
      .filter((artist) => {
        return searchRegex.test(artist.fullName ?? '');
      })
      .filter((artist) => {
        if (selectedStatus === 'all') {
          return true;
        }

        if (selectedStatus === 'client') {
          return artist.completedOnboardingSteps?.some(
            (step) => step.internalIdentifier === '3'
          );
        }

        if (selectedStatus === 'potential' && !artist.nonClient) {
          return !artist.completedOnboardingSteps?.some(
            (step) => step.internalIdentifier === '3'
          );
        }

        if (selectedStatus === 'non-client') {
          return artist.nonClient;
        }
      });

    return result.sort((a, b) => {
      if (sortOrder === 'asc') {
        return a.fullName?.localeCompare(b.fullName ?? '') ?? -1;
      }
      return b.fullName?.localeCompare(a.fullName ?? '') ?? 1;
    });
  }, [artists, artistsSearch, sortOrder, selectedStatus]);

  return (
    <div className="tw-mb-6 tw-w-full">
      <div className="tw-flex tw-justify-between">
        <span>Search</span>
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-gray-300">
          <select
            value={selectedStatus || ''}
            onChange={(e) => {
              const isClientStatus = (
                value: unknown
              ): value is ClientStatus => {
                return ['all', 'client', 'potential', 'non-client'].includes(
                  `${value}`
                );
              };
              const targetValue = e.target.value;
              if (isClientStatus(targetValue)) {
                setSelectedStatus(targetValue);
              }
            }}
            className="tw-bg-[#2c2c2c] tw-text-xs tw-rounded-lg tw-px-2 tw-py-1 tw-pr-7 tw-focus:outline-none tw-hover:bg-[#3c3c3c] tw-cursor-pointer tw-appearance-none tw-relative tw-min-w-[120px]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239CA3AF'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.25rem center',
              backgroundSize: '1.25em 1.25em',
            }}
          >
            <option className="tw-text-xs" value="all">
              All Clients
            </option>
            <option className="tw-text-xs" value="client">
              Client
            </option>
            <option className="tw-text-xs" value="potential">
              Potential Client
            </option>
            <option className="tw-text-xs" value="non-client">
              Non-Client
            </option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-bg-[#2c2c2c] tw-rounded-lg hover:tw-bg-[#3c3c3c] tw-transition-colors"
            title={`Sort ${sortOrder === 'asc' ? 'descending' : 'ascending'}`}
          >
            <span className="tw-text-xs tw-font-medium">Name</span>
            <span className="tw-text-xs tw-font-medium tw-opacity-75">
              {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
            </span>
          </button>
        </div>
      </div>
      <div className="tw-relative tw-mt-2">
        <i
          className={`wi wi-20px wi-search tw-absolute tw-left-3 tw-top-[14px]`}
        ></i>
        <input
          className={clsx(
            'tw-bg-formInputBackground',
            'tw-text-gray-400',
            'tw-rounded-lg',
            'tw-w-full',
            'tw-h-12',
            'tw-placeholder-textSeal',
            'tw-text-sm',
            'tw-leading-5',
            'tw-p-3',
            'tw-pl-9',
            'tw-mb-2',
            'tw-border-2',
            'tw-border-formInputBackground',
            'focus:tw-border-white',
            'focus:tw-text-white',
            'focus:tw-outline-none'
          )}
          name={Math.random().toString()} // prevent autofill
          placeholder="Search artists..."
          value={artistsSearch}
          autoComplete="off"
          onChange={(e) => setArtistsSearch(e.target.value)}
        />
      </div>
      <ul className="tw-max-h-[650px] tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-textDove tw-scrollbar-track-surfaceBlack90 tw-scrollbar-thumb-rounded-full">
        {filteredArtists.length > 0 ? (
          filteredArtists.map((artist) => {
            return (
              <li
                key={artist.userId}
                onClick={() => setClientId(artist?.userId)}
                className={`tw-flex tw-items-center tw-py-1.5 tw-px-2 tw-rounded-md tw-cursor-pointer tw-transition-colors
                ${
                  clientId === artist.userId
                    ? 'tw-bg-[#3c3c3c]'
                    : 'tw-hover:bg-[#3c3c3c]'
                }`}
              >
                <div className="tw-relative tw-mr-2">
                  <div
                    className={`tw-w-7 tw-h-7 tw-rounded-full tw-border-2 ${getStatusColor(
                      artist
                    )}`}
                  >
                    <img
                      // TODO: Avatar support has not been implemented yet
                      // src={artist?.image ? artist?.image : SquarePlaceholder100}
                      src={SquarePlaceholder100}
                      alt={artist?.fullName}
                      className="tw-rounded-full tw-w-full tw-h-full tw-object-cover"
                    />
                  </div>
                </div>
                <span className="tw-text-base">{artist?.fullName}</span>
              </li>
            );
          })
        ) : (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-8 tw-text-gray-400">
            <i
              className={`wi wi-20px wi-search tw-absolute tw-left-3 tw-top-[14px] tw-w-12 tw-h-12 tw-mb-3 tw-opacity-20`}
            ></i>
            <p className="tw-text-sm">No clients found</p>
            <p className="tw-text-xs tw-mt-1 tw-text-gray-500">
              Try adjusting your search or filter
            </p>
          </div>
        )}
      </ul>
    </div>
  );
};
